/**
 * Supplier-confirm API client — Lane 625 (Phase 5, screen 2).
 *
 * Two calls, both on endpoints that already ship:
 *   GET  /api/suppliers/search                                (Lane 45, §5.EV)
 *   POST /api/chat/upload/candidates/{id}/attach-supplier     (Lane 618)
 *
 * WHY THE SEARCH WRAPPER REPORTS FAILURE INSTEAD OF SWALLOWING IT
 * ---------------------------------------------------------------
 * The shipped chat picker (`components/SupplierDisambiguation.js`) does
 * `.then(r => r.ok ? r.json() : {results: []}).catch(() => callback([]))` — a
 * failed search renders as an EMPTY LIST, i.e. exactly the same thing a
 * genuinely-unmatched query renders as. In the chat picker that costs a
 * confused retry. Here it is worse: "no suppliers match" is the buyer's cue to
 * choose "not a supplier on file", which writes a resolution recording that
 * this letter came from nobody we hold. A search outage must not be able to
 * push a buyer into that.
 *
 * So this wrapper returns a discriminated result and the picker renders the
 * two cases differently. The interaction is the chat picker's; this one detail
 * is deliberately not.
 */

import { API_BASE } from '../config';

/** Mirrors the backend `Query(le=50)` cap on /api/suppliers/search. */
export const SUPPLIER_SEARCH_LIMIT = 25;

/**
 * Search the tenant's EXISTING suppliers.
 *
 * Never creates a supplier — the endpoint only ever selects from
 * `(company_id = X OR company_id IS NULL)`, which is the same pool the chat
 * picker draws from.
 *
 * Resolves to `{ ok, results }`:
 *   - `ok: true`  → `results` is the (possibly empty) match list
 *   - `ok: false` → the search itself failed; `results` is `[]` and the caller
 *                   must NOT present that as "no suppliers match"
 */
export async function searchSuppliers(authFetch, query, { limit = SUPPLIER_SEARCH_LIMIT } = {}) {
  const url =
    `${API_BASE}/api/suppliers/search` +
    `?q=${encodeURIComponent(query || '')}&limit=${limit}`;

  let response;
  try {
    response = await authFetch(url);
  } catch {
    // authFetch turns a 401 into a forced logout + throw; a transport failure
    // lands here too.
    return { ok: false, results: [] };
  }

  if (!response || !response.ok) return { ok: false, results: [] };

  try {
    const body = await response.json();
    const results = body && Array.isArray(body.results) ? body.results : [];
    return { ok: true, results };
  } catch {
    return { ok: false, results: [] };
  }
}

/**
 * Confirm which supplier sent a parked commercial notice.
 *
 * `supplierId === null` is a FIRST-CLASS request, not an omission: it is design
 * §1's "supplier not on file", which records the resolution, writes zero
 * assertions and leaves the candidate attachable. The endpoint's Pydantic model
 * declares `supplier_id: Optional[int]`, so `null` is the wire form.
 *
 * Two keys are sent, and only two: `supplier_id`, plus `conversation_id` when
 * the caller supplies one (Lane 657 / A1 — it is what makes the confirmed
 * supplier the CHAT conversation's subject, so a follow-up question inherits
 * it instead of starting from nothing). The model is `extra='forbid'`
 * (Rule 106), so adding `company_id` or `user_id` would earn a 422 rather than
 * a silent override — they come from the JWT. `conversation_id` is admissible
 * on the same footing as `supplier_id`: a client session handle, never used
 * for scoping or authorization.
 *
 * When no `conversationId` is passed the key is OMITTED, not sent as null, so
 * the request stays byte-identical to the pre-A1 shape.
 *
 * Resolves to `{ ok, httpStatus, body }`, and rejects only on a genuine
 * transport failure, so the caller can tell "the server said no" apart from
 * "the confirm never arrived" — which matters here, because a request that
 * never returns may still have been served.
 */
export async function attachSupplierToCandidate(
  authFetch, candidateId, supplierId, conversationId,
) {
  const url = `${API_BASE}/api/chat/upload/candidates/${encodeURIComponent(candidateId)}/attach-supplier`;

  const payload = { supplier_id: supplierId === undefined ? null : supplierId };
  // Only a non-empty string is worth sending. The server refuses a blank one
  // and refuses the shared 'default' sentinel anyway, so omitting it here just
  // keeps the request honest about what the client actually knows.
  if (typeof conversationId === 'string' && conversationId.trim()) {
    payload.conversation_id = conversationId;
  }

  const response = await authFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  let body = null;
  try {
    body = await response.json();
  } catch {
    // A non-JSON body (proxy error page, empty 502) must not throw — the caller
    // maps a missing body through describeAttachHttpError, which is honest
    // about not knowing rather than inventing a reason.
    body = null;
  }

  return { ok: response.ok, httpStatus: response.status, body };
}
